import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";

import useAuth from "../../../hooks/useAuth";
import Input from "../../form/Input";
import Select from "../../form/Select";
import FullscreenModal from "../fullscreen-modal";
import useAvailableProjectTypesOptions from "../../../views/settings/hooks/useAvailableProjectTypesOptions";
import {
  RECEIVING_WORKFLOW_OPTIONS,
  receivingWorkflowToMutationVariables,
} from "./projectReceivingWorkflow";
import MovewareStorageAccountField from "./MovewareStorageAccountField";

export function onKeyDown(event) {
  if (window.location.pathname.includes("/assets") && event.code === "Tab") {
    const inputs = [...document.querySelectorAll("input, select, button")];
    const index = inputs.findIndex(v => v === document.activeElement);
    inputs[index + 1].focus();
  }
}

export default function ClientModal({ initialValues, onSubmit, onClose }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const movewareEnabled = user.company.enabledIntegrationProviders?.includes("moveware");
  const projectTypeOptions = useAvailableProjectTypesOptions();

  const validationSchema = Yup.object().shape({
    clientName: Yup.string().required(`${settings.filter_field1} required`),
    projectName: Yup.string().required(`${settings.filter_field2} required`),
    ...(movewareEnabled && {
      movewareStorageAccountId: Yup.string()
        .trim()
        .matches(/^\d+$/, "Moveware Storage Account ID must contain only digits")
        .required("Moveware Storage Account ID required"),
      externalRecordId: Yup.string().required("Look up and confirm the Moveware storage account"),
    }),
    ...((settings.receiving_wizard || settings.project_types) && {
      projectType: Yup.string().required(`${settings.filter_field2} Field Template is required`),
    }),
  });

  const formikOnSubmit = (values, { setErrors, setStatus, setSubmitting }) => {
    setSubmitting(true);
    const {
      street,
      street2,
      city,
      zipCode,
      projectType,
      receivingWorkflow,
      ...rest
    } = values;
    delete rest.movewareStorageAccountId;
    const workflowVars = settings.receiving_wizard
      ? receivingWorkflowToMutationVariables(receivingWorkflow)
      : {};
    const variables = {
      ...rest,
      ...workflowVars,
      ...(projectType ? { projectType } : {}),
      addresses: [{ street, street2, city, zipCode }],
    };
    onSubmit({
      variables,
      onSuccess: () => {
        setSubmitting(false);
        setStatus({ success: true });
        onClose();
      },
      onFailure: errors => {
        setStatus({ success: false });
        setErrors(errors);
        setSubmitting(false);
      },
    });
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        movewareStorageAccountId: "",
        externalRecordId: "",
      }}
      validationSchema={validationSchema}
      onSubmit={formikOnSubmit}
    >
      {({ errors, handleBlur, isSubmitting, handleSubmit, values }) => (
        <form onSubmit={handleSubmit}>
          <FullscreenModal
            open
            id="client"
            title={`New ${settings.filter_field1} and ${settings.filter_field2}`}
            maxWidth={700}
            onClose={onClose}
            disableFullScreen
            actions={[
              <Button
                id="createClientAndProjectSubmit"
                key="create"
                variant="contained"
                color="primary"
                disabled={isSubmitting || (movewareEnabled && !values.externalRecordId)}
                type="submit"
                onClick={handleSubmit}
              >
                Create {settings.filter_field1} and {settings.filter_field2}
              </Button>,
            ]}
          >
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {movewareEnabled && <MovewareStorageAccountField populateClientName />}
              <Grid size={12}>
                <Input
                  autoFocus
                  name="clientName"
                  label={settings.filter_field1}
                  showNAButton={false}
                  inputProps={{ onKeyDown }}
                />
              </Grid>
              <Grid size={12}>
                <Input
                  name="projectName"
                  label={settings.filter_field2}
                  showNAButton={false}
                  inputProps={{ onKeyDown }}
                />
              </Grid>
              {(settings.receiving_wizard || settings.project_types) && (
                <Grid size={12}>
                  <Select
                    size="small"
                    name="projectType"
                    label={`${settings.filter_field2} Field Template`}
                    showNAButton={false}
                    options={projectTypeOptions}
                  />
                </Grid>
              )}
              {settings.receiving_wizard && (
                <Grid size={12}>
                  <Select
                    size="small"
                    name="receivingWorkflow"
                    label="Mode"
                    showNAButton={false}
                    options={RECEIVING_WORKFLOW_OPTIONS}
                  />
                </Grid>
              )}
              <>
                <Grid size={12}>
                    <Input
                      name="street"
                      label="Street"
                      showNAButton={false}
                      inputProps={{ onKeyDown }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Input
                      name="street2"
                      label="Second Street"
                      showNAButton={false}
                      inputProps={{ onKeyDown }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Input
                      name="city"
                      label="City"
                      showNAButton={false}
                      inputProps={{ onKeyDown }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Input
                      name="zipCode"
                      label="Zip"
                      showNAButton={false}
                      inputProps={{ onKeyDown }}
                    />
                </Grid>
              </>
            </Grid>
            {errors.submit && (
              <Box mt={2}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Box>
            )}
          </FullscreenModal>
        </form>
      )}
    </Formik>
  );
}
